import React, { useState, useEffect } from 'react';
import Button from './ui/Button';

/**
 * Componente: PaymentModal
 * Modal de registro de pagamento ao concluir um agendamento.
 * Captura forma de pagamento, valor, dados do cliente e agendamento futuro.
 */
const PaymentModal = ({ isOpen, onClose, appointment, onConfirm }) => {
  const _now = new Date();
  const today = `${_now.getFullYear()}-${String(_now.getMonth() + 1).padStart(2, '0')}-${String(_now.getDate()).padStart(2, '0')}`;
  const toTimeStr = (d) => d.toTimeString().slice(0, 5);

  const initialState = {
    // Valor e data
    amountCharged: '',         // valor cobrado pelo serviço
    paymentDate: today,        // data do pagamento

    // Forma de pagamento
    paymentMethod: 'cash',     // cash | pix | card

    // Dinheiro
    cashAmountReceived: '',    // quanto o cliente deu
    cashChange: '',            // troco (calculado)

    // Pix
    pixAccount: '',            // conta/chave que recebeu
    pixDate: today,            // data da transação pix
    pixTransactionId: '',      // id/comprovante (opcional)

    // Cartão
    cardType: 'debit',         // debit | credit
    cardInstallments: 1,       // número de parcelas
    cardHasInterest: false,    // cobrou juros?
    cardInterestRate: '',      // taxa de juros (%)
    cardFinalAmount: '',       // valor final com juros

    // Dados do cliente (pré-preenchidos)
    clientName: '',
    clientPhone: '',

    // Próximo agendamento
    scheduleNext: false,
    nextDate: '',
    nextTime: '',
    nextService: '',

    // Observações
    notes: '',
  };

  const [form, setForm] = useState(initialState);
  const [errors, setErrors] = useState({});

  // Pré-preencher dados do agendamento quando abrir
  useEffect(() => {
    if (isOpen && appointment) {
      const now = new Date();
      const defaultNextTime = toTimeStr(new Date(now.getTime() + 60 * 60 * 1000)); // +1h
      setForm(prev => ({
        ...initialState,
        amountCharged: appointment.servicePrice != null ? String(appointment.servicePrice) : '',
        clientName: appointment.clientName || '',
        clientPhone: appointment.clientPhone || '',
        nextService: appointment.service || '',
        nextTime: defaultNextTime,
      }));
      setErrors({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, appointment]);

  // Calcular troco automaticamente
  useEffect(() => {
    if (form.paymentMethod === 'cash') {
      const received = parseFloat(form.cashAmountReceived) || 0;
      const charged = parseFloat(form.amountCharged) || 0;
      const change = received - charged;
      setForm(prev => ({ ...prev, cashChange: change >= 0 ? change.toFixed(2) : '' }));
    }
  }, [form.cashAmountReceived, form.amountCharged, form.paymentMethod]);

  // Calcular valor final com juros
  useEffect(() => {
    if (form.paymentMethod === 'card' && form.cardType === 'credit' && form.cardHasInterest) {
      const base = parseFloat(form.amountCharged) || 0;
      const rate = parseFloat(form.cardInterestRate) || 0;
      const installments = parseInt(form.cardInstallments) || 1;
      if (base > 0 && rate > 0 && installments > 1) {
        // Juros simples por parcela
        const total = base * Math.pow(1 + rate / 100, installments);
        setForm(prev => ({ ...prev, cardFinalAmount: total.toFixed(2) }));
      } else if (base > 0 && !form.cardHasInterest) {
        setForm(prev => ({ ...prev, cardFinalAmount: base.toFixed(2) }));
      }
    }
  }, [form.amountCharged, form.cardHasInterest, form.cardInterestRate, form.cardInstallments, form.cardType, form.paymentMethod]);

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const validate = () => {
    const newErrors = {};
    if (!form.amountCharged || parseFloat(form.amountCharged) <= 0) {
      newErrors.amountCharged = 'Informe o valor cobrado';
    }
    if (!form.paymentDate) newErrors.paymentDate = 'Informe a data do pagamento';
    if (!form.clientName.trim()) newErrors.clientName = 'Informe o nome do cliente';

    if (form.paymentMethod === 'cash') {
      if (!form.cashAmountReceived || parseFloat(form.cashAmountReceived) < parseFloat(form.amountCharged || 0)) {
        newErrors.cashAmountReceived = 'Valor recebido deve ser igual ou maior ao cobrado';
      }
    }
    if (form.paymentMethod === 'pix') {
      if (!form.pixAccount.trim()) newErrors.pixAccount = 'Informe a conta/chave Pix';
      if (!form.pixDate) newErrors.pixDate = 'Informe a data do Pix';
    }
    if (form.paymentMethod === 'card') {
      if (form.cardType === 'credit' && form.cardInstallments > 1 && form.cardHasInterest) {
        if (!form.cardInterestRate || parseFloat(form.cardInterestRate) <= 0) {
          newErrors.cardInterestRate = 'Informe a taxa de juros';
        }
      }
    }
    if (form.scheduleNext) {
      if (!form.nextDate) newErrors.nextDate = 'Informe a data do próximo agendamento';
      if (!form.nextTime) newErrors.nextTime = 'Informe o horário';
      if (!form.nextService.trim()) newErrors.nextService = 'Informe o serviço';
    }
    return newErrors;
  };

  const handleSubmit = () => {
    const newErrors = validate();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    const payload = {
      appointmentId: appointment?.id,
      barbershopId: appointment?.barbershopId,
      barbershopName: appointment?.barbershopName || '',
      serviceType: appointment?.service || '',

      amountCharged: parseFloat(form.amountCharged),
      paymentDate: form.paymentDate,
      paymentMethod: form.paymentMethod,

      // Detalhes por método
      ...(form.paymentMethod === 'cash' && {
        cashAmountReceived: parseFloat(form.cashAmountReceived),
        cashChange: parseFloat(form.cashChange) || 0,
      }),
      ...(form.paymentMethod === 'pix' && {
        pixAccount: form.pixAccount.trim(),
        pixDate: form.pixDate,
        pixTransactionId: form.pixTransactionId.trim() || null,
      }),
      ...(form.paymentMethod === 'card' && {
        cardType: form.cardType,
        cardInstallments: parseInt(form.cardInstallments),
        cardHasInterest: form.cardType === 'credit' ? form.cardHasInterest : false,
        cardInterestRate: (form.cardType === 'credit' && form.cardHasInterest)
          ? parseFloat(form.cardInterestRate) : null,
        cardFinalAmount: (form.cardType === 'credit' && form.cardHasInterest && form.cardFinalAmount)
          ? parseFloat(form.cardFinalAmount)
          : parseFloat(form.amountCharged),
      }),

      // Cliente
      clientName: form.clientName.trim(),
      clientPhone: form.clientPhone.trim() || null,

      // Próximo agendamento
      scheduleNext: form.scheduleNext,
      ...(form.scheduleNext && {
        nextDate: form.nextDate,
        nextTime: form.nextTime,
        nextService: form.nextService.trim(),
      }),

      notes: form.notes.trim() || null,
      createdAt: new Date().toISOString(),
    };

    onConfirm(payload);
  };

  if (!isOpen) return null;

  const inputClass = (field) =>
    `w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
      errors[field] ? 'border-red-400 bg-red-50' : 'border-gray-300'
    }`;

  const labelClass = 'block text-sm font-medium text-gray-700 mb-1';

  const effectiveTotal = (() => {
    if (form.paymentMethod === 'card' && form.cardType === 'credit' && form.cardHasInterest && form.cardFinalAmount) {
      return parseFloat(form.cardFinalAmount);
    }
    return parseFloat(form.amountCharged) || 0;
  })();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-green-600 to-emerald-600 text-white p-4 sm:p-6 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <div className="flex-1 min-w-0 mr-3">
              <h2 className="text-lg sm:text-xl font-bold">✅ Registrar Pagamento</h2>
              <p className="text-green-100 text-xs sm:text-sm mt-1 truncate">
                {appointment?.clientName} — {appointment?.service}
              </p>
            </div>
            <button
              onClick={onClose}
              className="shrink-0 text-white hover:text-green-200 transition-colors text-2xl font-bold leading-none"
            >
              ×
            </button>
          </div>
        </div>

        <div className="p-4 sm:p-6 space-y-5">

          {/* === VALOR E DATA === */}
          <div className="bg-gray-50 rounded-xl p-4">
            <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
              💰 Valor e Data
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>Valor cobrado *</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">R$</span>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    className={`${inputClass('amountCharged')} pl-10`}
                    value={form.amountCharged}
                    onChange={e => handleChange('amountCharged', e.target.value)}
                    placeholder="0,00"
                  />
                </div>
                {errors.amountCharged && <p className="text-red-500 text-xs mt-1">{errors.amountCharged}</p>}
              </div>
              <div>
                <label className={labelClass}>Data do pagamento *</label>
                <input
                  type="date"
                  className={inputClass('paymentDate')}
                  value={form.paymentDate}
                  onChange={e => handleChange('paymentDate', e.target.value)}
                />
                {errors.paymentDate && <p className="text-red-500 text-xs mt-1">{errors.paymentDate}</p>}
              </div>
            </div>
          </div>

          {/* === DADOS DO CLIENTE === */}
          <div className="bg-gray-50 rounded-xl p-4">
            <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
              👤 Dados do Cliente
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>Nome *</label>
                <input
                  type="text"
                  className={inputClass('clientName')}
                  value={form.clientName}
                  onChange={e => handleChange('clientName', e.target.value)}
                  placeholder="Nome do cliente"
                />
                {errors.clientName && <p className="text-red-500 text-xs mt-1">{errors.clientName}</p>}
              </div>
              <div>
                <label className={labelClass}>Telefone / WhatsApp</label>
                <input
                  type="tel"
                  className={inputClass('clientPhone')}
                  value={form.clientPhone}
                  onChange={e => handleChange('clientPhone', e.target.value)}
                  placeholder="(11) 99999-9999"
                />
              </div>
            </div>
          </div>

          {/* === FORMA DE PAGAMENTO === */}
          <div className="bg-gray-50 rounded-xl p-4">
            <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
              💳 Forma de Pagamento
            </h3>

            {/* Seletor */}
            <div className="grid grid-cols-3 gap-3 mb-4">
              {[
                { value: 'cash', label: '💵 Dinheiro' },
                { value: 'pix', label: '🔑 Pix' },
                { value: 'card', label: '💳 Cartão' },
              ].map(opt => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => handleChange('paymentMethod', opt.value)}
                  className={`py-2.5 rounded-lg text-sm font-medium border-2 transition-all ${
                    form.paymentMethod === opt.value
                      ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                      : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            {/* DINHEIRO */}
            {form.paymentMethod === 'cash' && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>Quanto o cliente deu *</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">R$</span>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      className={`${inputClass('cashAmountReceived')} pl-10`}
                      value={form.cashAmountReceived}
                      onChange={e => handleChange('cashAmountReceived', e.target.value)}
                      placeholder="0,00"
                    />
                  </div>
                  {errors.cashAmountReceived && <p className="text-red-500 text-xs mt-1">{errors.cashAmountReceived}</p>}
                </div>
                <div>
                  <label className={labelClass}>Troco a dar</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">R$</span>
                    <input
                      type="text"
                      readOnly
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm pl-10 bg-green-50 text-green-700 font-semibold"
                      value={form.cashChange || '0,00'}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* PIX */}
            {form.paymentMethod === 'pix' && (
              <div className="space-y-3">
                <div>
                  <label className={labelClass}>Chave/Conta que recebeu *</label>
                  <input
                    type="text"
                    className={inputClass('pixAccount')}
                    value={form.pixAccount}
                    onChange={e => handleChange('pixAccount', e.target.value)}
                    placeholder="CPF, e-mail, telefone ou chave aleatória"
                  />
                  {errors.pixAccount && <p className="text-red-500 text-xs mt-1">{errors.pixAccount}</p>}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className={labelClass}>Data da transação Pix *</label>
                    <input
                      type="date"
                      className={inputClass('pixDate')}
                      value={form.pixDate}
                      onChange={e => handleChange('pixDate', e.target.value)}
                    />
                    {errors.pixDate && <p className="text-red-500 text-xs mt-1">{errors.pixDate}</p>}
                  </div>
                  <div>
                    <label className={labelClass}>ID / Comprovante (opcional)</label>
                    <input
                      type="text"
                      className={inputClass('pixTransactionId')}
                      value={form.pixTransactionId}
                      onChange={e => handleChange('pixTransactionId', e.target.value)}
                      placeholder="Nº do comprovante"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* CARTÃO */}
            {form.paymentMethod === 'card' && (
              <div className="space-y-4">
                {/* Tipo */}
                <div>
                  <label className={labelClass}>Tipo de cartão *</label>
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { value: 'debit', label: '🏦 Débito' },
                      { value: 'credit', label: '💳 Crédito' },
                    ].map(opt => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => handleChange('cardType', opt.value)}
                        className={`py-2 rounded-lg text-sm font-medium border-2 transition-all ${
                          form.cardType === opt.value
                            ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                            : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Parcelamento - somente crédito */}
                {form.cardType === 'credit' && (
                  <div className="space-y-3">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className={labelClass}>Número de parcelas</label>
                        <select
                          className={inputClass('cardInstallments')}
                          value={form.cardInstallments}
                          onChange={e => handleChange('cardInstallments', parseInt(e.target.value))}
                        >
                          {[1,2,3,4,5,6,7,8,9,10,11,12].map(n => (
                            <option key={n} value={n}>{n}x {n === 1 ? '(à vista)' : ''}</option>
                          ))}
                        </select>
                      </div>
                      {form.cardInstallments > 1 && (
                        <div className="flex items-end">
                          <label className="flex items-center gap-2 cursor-pointer pb-2">
                            <input
                              type="checkbox"
                              checked={form.cardHasInterest}
                              onChange={e => handleChange('cardHasInterest', e.target.checked)}
                              className="w-4 h-4 accent-indigo-600"
                            />
                            <span className="text-sm text-gray-700">Cobrou juros?</span>
                          </label>
                        </div>
                      )}
                    </div>

                    {form.cardInstallments > 1 && form.cardHasInterest && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                        <div>
                          <label className={labelClass}>Taxa de juros (% ao mês) *</label>
                          <div className="relative">
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              className={`${inputClass('cardInterestRate')} pr-8`}
                              value={form.cardInterestRate}
                              onChange={e => handleChange('cardInterestRate', e.target.value)}
                              placeholder="0,00"
                            />
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">%</span>
                          </div>
                          {errors.cardInterestRate && <p className="text-red-500 text-xs mt-1">{errors.cardInterestRate}</p>}
                        </div>
                        <div>
                          <label className={labelClass}>Valor final recebido</label>
                          <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">R$</span>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              className={`${inputClass('cardFinalAmount')} pl-10`}
                              value={form.cardFinalAmount}
                              onChange={e => handleChange('cardFinalAmount', e.target.value)}
                              placeholder="Calculado automaticamente"
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* === PRÓXIMO AGENDAMENTO === */}
          <div className="bg-gray-50 rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                📅 Agendar próxima visita
              </h3>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.scheduleNext}
                  onChange={e => handleChange('scheduleNext', e.target.checked)}
                  className="w-4 h-4 accent-indigo-600"
                />
                <span className="text-sm text-gray-600">Ativar</span>
              </label>
            </div>

            {form.scheduleNext && (
              <div className="space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className={labelClass}>Data *</label>
                    <input
                      type="date"
                      className={inputClass('nextDate')}
                      value={form.nextDate}
                      min={today}
                      onChange={e => handleChange('nextDate', e.target.value)}
                    />
                    {errors.nextDate && <p className="text-red-500 text-xs mt-1">{errors.nextDate}</p>}
                  </div>
                  <div>
                    <label className={labelClass}>Horário *</label>
                    <input
                      type="time"
                      className={inputClass('nextTime')}
                      value={form.nextTime}
                      onChange={e => handleChange('nextTime', e.target.value)}
                    />
                    {errors.nextTime && <p className="text-red-500 text-xs mt-1">{errors.nextTime}</p>}
                  </div>
                </div>
                <div>
                  <label className={labelClass}>Serviço *</label>
                  <input
                    type="text"
                    className={inputClass('nextService')}
                    value={form.nextService}
                    onChange={e => handleChange('nextService', e.target.value)}
                    placeholder="Ex: Corte + Barba"
                  />
                  {errors.nextService && <p className="text-red-500 text-xs mt-1">{errors.nextService}</p>}
                </div>
              </div>
            )}
          </div>

          {/* === OBSERVAÇÕES === */}
          <div className="bg-gray-50 rounded-xl p-4">
            <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
              📝 Observações
            </h3>
            <textarea
              className={`${inputClass('notes')} resize-none`}
              rows={3}
              value={form.notes}
              onChange={e => handleChange('notes', e.target.value)}
              placeholder="Anotações adicionais sobre o atendimento..."
            />
          </div>

          {/* === RESUMO === */}
          <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4">
            <h3 className="font-semibold text-indigo-800 mb-3">📋 Resumo do pagamento</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Método:</span>
                <span className="font-medium text-gray-800">
                  {form.paymentMethod === 'cash' && '💵 Dinheiro'}
                  {form.paymentMethod === 'pix' && '🔑 Pix'}
                  {form.paymentMethod === 'card' && `💳 Cartão ${form.cardType === 'credit' ? 'Crédito' : 'Débito'}${form.cardType === 'credit' && form.cardInstallments > 1 ? ` ${form.cardInstallments}x` : ''}`}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Valor cobrado:</span>
                <span className="font-medium text-gray-800">R$ {parseFloat(form.amountCharged || 0).toFixed(2)}</span>
              </div>
              {form.paymentMethod === 'card' && form.cardType === 'credit' && form.cardHasInterest && form.cardFinalAmount && (
                <div className="flex justify-between text-amber-700">
                  <span>Valor final (com juros):</span>
                  <span className="font-semibold">R$ {parseFloat(form.cardFinalAmount).toFixed(2)}</span>
                </div>
              )}
              {form.paymentMethod === 'cash' && form.cashChange && (
                <div className="flex justify-between text-green-700">
                  <span>Troco:</span>
                  <span className="font-semibold">R$ {form.cashChange}</span>
                </div>
              )}
              <div className="flex justify-between border-t pt-2 mt-2">
                <span className="font-semibold text-indigo-800">Total efetivo recebido:</span>
                <span className="font-bold text-indigo-700 text-base">R$ {effectiveTotal.toFixed(2)}</span>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <Button
              variant="secondary"
              onClick={onClose}
              className="flex-1"
            >
              Cancelar
            </Button>
            <Button
              variant="success"
              onClick={handleSubmit}
              className="flex-1"
            >
              ✅ Confirmar e Concluir
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentModal;
